import React from 'react';
import { DollarSign, Percent, Users, Wallet } from 'lucide-react';

export type RegionalKpiStripMode = 'overview' | 'region';

export interface RegionalKpiStripProps {
  mode: RegionalKpiStripMode;
  totalRevenue: number;
  collectionPct: number;
  totalClients: number;
  totalOutstanding: number;
  totalInvoiced: number;
  /** Overview: number of regions in the table */
  regionCount?: number;
  /** Override first overview revenue tile subline (e.g. entity page). */
  revenueSubtext?: string;
  /** Override overview outstanding tile subline (e.g. entity page). */
  outstandingSubtext?: string;
  /** Region drill-down: tier line */
  tierSubtext?: string;
  clientsWithOutstanding?: number;
}

const fmtUsd = (n: number) =>
  `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export function RegionalKpiStrip({
  mode,
  totalRevenue,
  collectionPct,
  totalClients,
  totalOutstanding,
  totalInvoiced,
  regionCount,
  revenueSubtext,
  outstandingSubtext,
  tierSubtext,
  clientsWithOutstanding,
}: RegionalKpiStripProps) {
  const invoicedK =
    totalInvoiced > 0 ? (totalInvoiced / 1000).toFixed(0) : '0';

  const tiles = [
    {
      label: 'Total revenue',
      value: fmtUsd(totalRevenue),
      sub:
        mode === 'overview'
          ? revenueSubtext ?? 'Cross-region total · USD'
          : 'Paid (USD) · selected region',
      accent: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bg: 'bg-emerald-50',
      icon: <DollarSign size={16} className="text-emerald-500" />,
    },
    {
      label: 'Collection rate',
      value: `${collectionPct.toFixed(1)}%`,
      sub:
        totalInvoiced > 0
          ? `Invoiced ${fmtUsd(totalInvoiced)} (${invoicedK}K)`
          : 'Paid ÷ invoiced',
      accent: 'bg-violet-500',
      textColor: 'text-violet-700',
      bg: 'bg-violet-50',
      icon: <Percent size={16} className="text-violet-500" />,
    },
    {
      label: 'Clients',
      value: String(totalClients),
      sub:
        mode === 'overview'
          ? 'Unique clients in period'
          : tierSubtext || 'Unique in region',
      accent: 'bg-blue-500',
      textColor: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: <Users size={16} className="text-blue-500" />,
    },
    {
      label: 'Outstanding',
      value: fmtUsd(totalOutstanding),
      sub:
        mode === 'overview'
          ? outstandingSubtext ??
            (regionCount != null
              ? `${regionCount} region${regionCount !== 1 ? 's' : ''} · USD roll-up (table shows local where available)`
              : 'Open balance · USD roll-up')
          : clientsWithOutstanding != null
            ? `${clientsWithOutstanding} with balance`
            : 'Open balance (USD)',
      accent: 'bg-amber-500',
      textColor: 'text-amber-700',
      bg: 'bg-amber-50',
      icon: <Wallet size={16} className="text-amber-500" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {tiles.map(tile => (
        <div
          key={tile.label}
          className="bg-white rounded-xl border border-slate-200 shadow-md p-4 flex gap-3 items-start"
        >
          <div className={`w-1 self-stretch rounded-full flex-none ${tile.accent}`} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              {tile.label}
            </p>
            <p
              className={`text-xl font-bold tabular-nums leading-tight ${tile.textColor}`}
            >
              {tile.value}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              {tile.sub}
            </p>
          </div>
          <div
            className={`w-8 h-8 rounded-lg ${tile.bg} flex items-center justify-center flex-none`}
          >
            {tile.icon}
          </div>
        </div>
      ))}
    </div>
  );
}
